import { apiDelete, apiGet, apiPost, apiPut } from '@/shared/utils/requestBuilder';
import type {
  Categoria,
  CategoriaFilters,
  CreateCategoriaDto,
  UpdateCategoriaDto,
} from '@/types/categoria/categoriaTypes';

export const categoriaService = {
  listar: async (filters?: CategoriaFilters): Promise<{ data: Categoria[]; total: number }> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiGet(`/categorias${query}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao listar categorias' }));
      throw new Error(errorData.error || 'Erro ao listar categorias');
    }

    const result = await response.json();
    return {
      data: result.data.data,
      total: result.data.pagination?.total || 0,
    };
  },

  buscarPorId: async (id: string): Promise<Categoria> => {
    const response = await apiGet(`/categorias/${id}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Categoria não encontrada' }));
      throw new Error(errorData.error || 'Categoria não encontrada');
    }

    const result = await response.json();
    return result.data;
  },

  criar: async (data: CreateCategoriaDto): Promise<Categoria> => {
    const response = await apiPost('/categorias', data);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao criar categoria' }));
      throw new Error(errorData.error || 'Erro ao criar categoria');
    }

    const result = await response.json();
    return result.data;
  },

  atualizar: async (id: string, data: UpdateCategoriaDto): Promise<Categoria> => {
    const response = await apiPut(`/categorias/${id}`, data);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Erro ao atualizar categoria' }));
      throw new Error(errorData.error || 'Erro ao atualizar categoria');
    }

    const result = await response.json();
    return result.data;
  },

  excluir: async (id: string): Promise<void> => {
    const response = await apiDelete(`/categorias/${id}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao excluir categoria' }));
      throw new Error(errorData.error || 'Erro ao excluir categoria');
    }
  },

  buscarEstatisticas: async (
    id: string,
  ): Promise<{
    totalExecucoes: number;
    valorTotal: number;
    execucoesAtivas: number;
    execucoesPausadas: number;
    execucoesEncerradas: number;
  }> => {
    const response = await apiGet(`/categorias/${id}/statistics`);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Erro ao buscar estatísticas' }));
      throw new Error(errorData.error || 'Erro ao buscar estatísticas');
    }

    const result = await response.json();
    return result.data;
  },

  buscarTimeline: async (
    id: string,
    filters?: {
      page?: number;
      limit?: number;
      tipoEvento?: string;
    },
  ): Promise<{
    data: Array<{
      id: string;
      tipoEvento: string;
      descricao: string;
      usuarioId: string | null;
      usuarioNome: string;
      usuarioEmail: string;
      dataEvento: string;
      metadados: Record<string, unknown>;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> => {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.tipoEvento) params.append('tipoEvento', filters.tipoEvento);

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiGet(`/categorias/${id}/timeline${query}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro ao buscar timeline' }));
      throw new Error(errorData.error || 'Erro ao buscar timeline');
    }

    const result = await response.json();
    return result.data;
  },
};
